import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Home, CheckSquare, Target, Wallet, FileText, Dumbbell,
  ChevronLeft, ChevronRight, LogOut, Zap, Settings, Activity, X
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";

// Sessão UI-2 — Sidebar consolidado: 9 items (era 12).
// "Rotina" substitui Lembretes + Calendário + Hábitos + Projetos.
// Componentes legacy permanecem no repo (back-compat) — apenas
// removidos do menu. Hash redirect (#habits etc → #rotina/...) é
// tratado no NectarDashboard.
const navItems = [
  { id: "home",        label: "Visão Geral", icon: Home },
  { id: "rotina",      label: "Rotina",      icon: Target },
  { id: "tasks",       label: "Tarefas",     icon: CheckSquare },
  { id: "finance",     label: "Financeiro",  icon: Wallet },
  { id: "notes",       label: "Notas",       icon: FileText },
  { id: "automations", label: "Automações",  icon: Zap },
  { id: "training",    label: "Treino",      icon: Dumbbell },
  { id: "settings",    label: "Configurações", icon: Settings },
  { id: "admin-agent", label: "Telemetria",  icon: Activity, adminOnly: true, route: "/admin/agent" },
];

// Shared easing for all transitions in the shell (matches CSS ease-smooth token).
const EASE_SMOOTH = [0.2, 0.8, 0.2, 1];

/**
 * Sidebar — variant "permanent" (desktop) ou "drawer" (mobile).
 *
 * Desktop: aside fixed esquerda, expansível 72px↔240px. Comportamento legado.
 * Mobile: drawer overlay com backdrop, slide-in da esquerda 280px (max 85vw).
 *         Fecha automaticamente ao navegar ou tap no backdrop.
 *
 * Mobile Parity Epic — Etapa 1.
 */
export default function Sidebar({
  activeSection,
  onSectionChange,
  isExpanded,
  onToggleExpand,
  variant = "permanent",
  isDrawerOpen = false,
  onDrawerClose = () => {},
}) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const visibleNavItems = navItems.filter(item => !item.adminOnly || user?.is_admin);

  // === DRAWER MODE (mobile) ===
  if (variant === "drawer") {
    const handleItemClick = (item) => {
      if (item.route) navigate(item.route);
      else onSectionChange(item.id);
      // onSectionChange já chama onDrawerClose pelo wrapper do MainLayout;
      // aqui garantimos fechar também em rotas externas.
      onDrawerClose();
    };

    return (
      <AnimatePresence>
        {isDrawerOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={onDrawerClose}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
              data-testid="sidebar-drawer-backdrop"
            />
            <motion.aside
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ duration: 0.25, ease: EASE_SMOOTH }}
              className="fixed left-0 top-0 h-screen w-[280px] max-w-[85vw] bg-surface-raised border-r border-subtle z-50 flex flex-col safe-area-pt safe-area-pl"
              data-testid="sidebar-drawer"
            >
              {/* Header drawer: logo + close */}
              <div className="h-16 flex items-center justify-between px-4 border-b border-subtle">
                <div className="flex items-center gap-2">
                  <img
                    src="/logo-nav.png"
                    alt="Nexo"
                    className="w-10 h-10 rounded-lg object-cover border border-brand-cyan/30"
                    style={{ boxShadow: "0 0 12px rgba(0, 212, 255, 0.2)" }}
                  />
                  <span className="font-display font-bold text-xl text-foreground tracking-wider">
                    NEXO
                  </span>
                </div>
                <button
                  type="button"
                  onClick={onDrawerClose}
                  aria-label="Fechar menu"
                  className="p-2 -mr-2 rounded-lg hover:bg-white/5 text-foreground-muted hover:text-foreground transition-colors"
                  data-testid="sidebar-drawer-close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Nav items — labels sempre visíveis em drawer */}
              <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
                {visibleNavItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = !item.route && activeSection === item.id;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => handleItemClick(item)}
                      className={cn(
                        "w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-colors duration-normal ease-smooth border text-left",
                        isActive
                          ? "bg-brand-cyan/10 text-brand-cyan border-brand-cyan/30 shadow-glow-cyan"
                          : "text-foreground-muted hover:text-foreground hover:bg-surface-card-hover border-transparent"
                      )}
                      data-testid={`nav-drawer-${item.id}`}
                    >
                      <Icon className={cn("w-5 h-5 flex-shrink-0", isActive && "text-brand-cyan")} />
                      <span className="text-sm font-medium">{item.label}</span>
                    </button>
                  );
                })}
              </nav>

              {/* Footer: logout */}
              <div className="p-3 border-t border-subtle safe-area-pb">
                {user && (
                  <button
                    type="button"
                    onClick={() => { logout(); onDrawerClose(); }}
                    className="w-full flex items-center gap-3 p-3 rounded-lg text-status-error/70 hover:text-status-error hover:bg-status-error/10 transition-colors duration-normal ease-smooth"
                    data-testid="sidebar-drawer-logout"
                  >
                    <LogOut className="w-5 h-5 flex-shrink-0" />
                    <span className="text-sm font-medium">Sair</span>
                  </button>
                )}
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    );
  }

  // === PERMANENT MODE (desktop) — código original intacto ===
  return (
    <motion.aside
      initial={false}
      animate={{ width: isExpanded ? 240 : 72 }}
      transition={{ duration: 0.2, ease: EASE_SMOOTH }}
      className="fixed left-0 top-0 h-screen bg-surface-raised border-r border-subtle z-30 flex flex-col"
      data-testid="sidebar"
    >
      {/* Logo */}
      <div className="h-16 flex items-center justify-center border-b border-subtle">
        <div className="flex items-center gap-2">
          <img
            src="/logo-nav.png"
            alt="Nexo"
            className="w-10 h-10 rounded-lg object-cover border border-brand-cyan/30"
            style={{ boxShadow: "0 0 12px rgba(0, 212, 255, 0.2)" }}
          />
          <AnimatePresence>
            {isExpanded && (
              <motion.span
                initial={{ opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: "auto" }}
                exit={{ opacity: 0, width: 0 }}
                className="font-display font-bold text-xl text-foreground tracking-wider overflow-hidden whitespace-nowrap"
              >
                NEXO
              </motion.span>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 px-2 space-y-1 overflow-y-auto">
        {visibleNavItems.map((item) => {
          const Icon = item.icon;
          const isActive = !item.route && activeSection === item.id;

          return (
            <motion.button
              key={item.id}
              onClick={() => item.route ? navigate(item.route) : onSectionChange(item.id)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={cn(
                "w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-colors duration-normal ease-smooth border",
                isActive
                  ? "bg-brand-cyan/10 text-brand-cyan border-brand-cyan/30 shadow-glow-cyan"
                  : "text-foreground-muted hover:text-foreground hover:bg-surface-card-hover border-transparent"
              )}
              data-testid={`nav-${item.id}`}
            >
              <Icon className={cn("w-5 h-5 flex-shrink-0", isActive && "text-brand-cyan")} />
              <AnimatePresence>
                {isExpanded && (
                  <motion.span
                    initial={{ opacity: 0, width: 0 }}
                    animate={{ opacity: 1, width: "auto" }}
                    exit={{ opacity: 0, width: 0 }}
                    className="text-sm font-medium overflow-hidden whitespace-nowrap"
                  >
                    {item.label}
                  </motion.span>
                )}
              </AnimatePresence>
            </motion.button>
          );
        })}
      </nav>

      {/* Logout + Toggle */}
      <div className="p-2 border-t border-subtle space-y-1">
        {user && (
          <motion.button
            onClick={logout}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            className="w-full flex items-center gap-3 p-3 rounded-lg text-status-error/70 hover:text-status-error hover:bg-status-error/10 transition-colors duration-normal ease-smooth"
            data-testid="sidebar-logout"
          >
            <LogOut className="w-5 h-5 flex-shrink-0" />
            <AnimatePresence>
              {isExpanded && (
                <motion.span
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: "auto" }}
                  exit={{ opacity: 0, width: 0 }}
                  className="text-sm font-medium overflow-hidden whitespace-nowrap"
                >
                  Sair
                </motion.span>
              )}
            </AnimatePresence>
          </motion.button>
        )}
        <motion.button
          onClick={onToggleExpand}
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          className="w-full flex items-center justify-center p-3 rounded-lg text-foreground-muted hover:text-foreground hover:bg-surface-card-hover transition-colors duration-normal ease-smooth"
          data-testid="sidebar-toggle"
        >
          {isExpanded ? <ChevronLeft className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
        </motion.button>
      </div>
    </motion.aside>
  );
}
